import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  LinearProgress,
  Slider,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import DifferenceIcon from '@mui/icons-material/Difference';

import { OperationService } from '../../../services';
import type { OperationResult, SourceImageRef } from '../../../domain';
import {
  DEFAULT_VARIATIONS_STATE,
  buildVariationsRequest,
  hasSourceImage,
  type VariationsFormState,
} from './buildRequests';
import { useOperationMutation } from './useOperationMutation';
import { submitErrorMessage } from '../../../components/auth/signInRequiredError';
import type { StudioParams } from '../../shared/panels/swatchSelection';
import { StudioSwatchChips } from './StudioSwatchChips';
import { ProviderModelPicker } from './ProviderModelPicker';

interface Props {
  ensureSession: () => Promise<string>;
  isCreatingSession?: boolean;
  source: SourceImageRef | undefined;
  /** Variations has no prompt field, so swatches don't shape the request.
   *  We still render the chip row so the user sees the active Studio state. */
  studioParams?: StudioParams;
  onResult?: (result: OperationResult) => void;
}

export const VariationsPanel: React.FC<Props> = ({
  ensureSession,
  isCreatingSession = false,
  source,
  studioParams,
  onResult,
}) => {
  const [state, setState] = useState<VariationsFormState>(DEFAULT_VARIATIONS_STATE);
  const [validationError, setValidationError] = useState<string | null>(null);

  const mutation = useOperationMutation(OperationService.variations);

  const hasSource = hasSourceImage(source);

  const handleSubmit = async () => {
    if (!source) {
      setValidationError('A source image is required to generate variations.');
      return;
    }
    try {
      const sid = await ensureSession();
      const request = buildVariationsRequest(state, sid, source);
      setValidationError(null);
      const result = await mutation.mutateAsync(request);
      onResult?.(result);
    } catch (err) {
      setValidationError(submitErrorMessage(err));
    }
  };

  const submitDisabled = mutation.isPending || isCreatingSession || !hasSource;

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} alignItems="center">
        <DifferenceIcon fontSize="small" />
        <Typography variant="subtitle2">Variations</Typography>
      </Stack>

      {!hasSource && (
        <Alert severity="info">Add or select a source image to vary.</Alert>
      )}

      <StudioSwatchChips studioParams={studioParams} hint="not applied to Variations" />

      <ProviderModelPicker
        model={state.model}
        onModelChange={m => setState(s => ({ ...s, model: m }))}
        disabled={mutation.isPending || isCreatingSession}
        label="Image model"
      />

      <TextField
        label="Variations"
        type="number"
        value={state.numVariations}
        onChange={e => setState(s => ({ ...s, numVariations: Number(e.target.value) }))}
        size="small"
        inputProps={{ min: 1, max: 8, step: 1 }}
      />

      <Box>
        <Typography variant="caption" color="text.secondary">
          Variation strength · {state.variationStrength.toFixed(2)}
        </Typography>
        <Slider
          value={state.variationStrength}
          onChange={(_, v) =>
            setState(s => ({ ...s, variationStrength: Array.isArray(v) ? v[0] : v }))
          }
          min={0}
          max={1}
          step={0.05}
          size="small"
        />
      </Box>

      <FormControlLabel
        control={
          <Switch
            checked={state.preserveStyle}
            onChange={e => setState(s => ({ ...s, preserveStyle: e.target.checked }))}
            size="small"
          />
        }
        label="Preserve style"
      />

      {validationError && <Alert severity="warning">{validationError}</Alert>}
      {mutation.isError && (
        <Alert severity="error">
          {mutation.error instanceof Error ? mutation.error.message : 'Variations failed.'}
        </Alert>
      )}

      {mutation.isPending && (
        <Alert severity="info" icon={<DifferenceIcon fontSize="inherit" />}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Generating {state.numVariations} {state.numVariations === 1 ? 'variation' : 'variations'}…
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Each variation is rendered separately, so this takes longer than a single image.
          </Typography>
          <LinearProgress sx={{ mt: 1 }} />
        </Alert>
      )}

      <Box>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitDisabled}
          startIcon={
            mutation.isPending || isCreatingSession ? (
              <CircularProgress size={16} />
            ) : (
              <DifferenceIcon />
            )
          }
        >
          {isCreatingSession
            ? 'Starting session…'
            : mutation.isPending
              ? `Generating ${state.numVariations} ${state.numVariations === 1 ? 'variation' : 'variations'}…`
              : 'Generate variations'}
        </Button>
      </Box>
    </Stack>
  );
};
